/**
 * useOwnerEra (OWN-ERA, 2026-06-16) — the ONE owner-attribution source for the 360.
 *
 * A reused ESC account can change hands two ways Pegasus knows about:
 *   1. a RECORDED transfer — someone did "New owner" on the header → a row in
 *      `pegasus_property_ownership`. Authoritative (a human stated it).
 *   2. a DERIVED chimera split — note timestamps date the handoff (BUILD-17 / DEC-26,
 *      `useOwnerVisitSplit`). Best-guess context until someone records the real transfer.
 *
 * Per the ownership design (research/property-ownership-lifecycle.md §8 — "unify the two
 * ownership representations"), the RECORDED transfer wins; the derived split is the fallback
 * when nothing's been recorded yet. Every tab (Overview, Invoices, History, Equipment) asks
 * this one hook "who owned this on <date>?" so the prev-owner badge is consistent everywhere —
 * and null (no badge, zero clutter) for the ~99% of properties that never changed hands.
 */
import { useLocationOwnership, priorOwnerForDate } from "@/features/jobs/properties";
import { useOwnerVisitSplit } from "./api";

export interface OwnerEra {
  /** True once we know of a handoff (recorded or derived). */
  hasTransfer: boolean;
  /** The handoff date — the current owner's tenure start, or the derived note date. */
  handoffDate: string | null;
  /** The most-recent previous owner's display name. */
  priorOwnerName: string | null;
  /** The current owner's recorded name (recorded transfers only). */
  currentOwnerName: string | null;
  /** Where the attribution came from (recorded transfer beats derived note-split). */
  source: "recorded" | "derived" | null;
  /** Name the owner a dated record belongs to IF it predates the current owner — else null. */
  priorOwnerFor: (date: string | null | undefined) => string | null;
}

export function useOwnerEra(
  custno: string | null | undefined,
  locno: string | null | undefined,
): OwnerEra {
  const { data: owned } = useLocationOwnership(custno, locno);
  const { data: split } = useOwnerVisitSplit(custno ?? undefined, locno);

  const previousOwners = owned?.previousOwners ?? [];
  const open = owned?.ownership.find((r) => !r.end_date && r.role === "owner")
    ?? owned?.ownership.find((r) => !r.end_date && r.role !== "builder");
  const recordedStart = open?.start_date ?? null;

  // 1. A recorded transfer is the truth — use it.
  if (previousOwners.length > 0 && recordedStart) {
    return {
      hasTransfer: true,
      handoffDate: recordedStart,
      priorOwnerName: previousOwners[0].owner.name,
      currentOwnerName: owned?.currentOwner?.name ?? open?.party_name ?? null,
      source: "recorded",
      priorOwnerFor: (date) => priorOwnerForDate(date, recordedStart, previousOwners),
    };
  }
  // 2. Else fall back to the note-derived chimera split.
  if (split?.handoff_date) {
    const handoff = split.handoff_date;
    return {
      hasTransfer: true,
      handoffDate: handoff,
      priorOwnerName: split.prior_owner,
      currentOwnerName: null,
      source: "derived",
      priorOwnerFor: (date) => (date && date < handoff ? split.prior_owner : null),
    };
  }
  // 3. Single-owner home — no attribution, no badge anywhere.
  return {
    hasTransfer: false,
    handoffDate: null,
    priorOwnerName: null,
    currentOwnerName: null,
    source: null,
    priorOwnerFor: () => null,
  };
}

/** The shared "prev. owner" chip — one look everywhere a record predates the current owner. */
export function PriorOwnerBadge({
  name,
  className = "",
}: {
  name: string | null | undefined;
  className?: string;
}) {
  if (!name) return null;
  return (
    <span
      className={`inline-flex max-w-full items-center truncate rounded bg-amber-50 px-1.5 py-px text-[10px] font-medium text-amber-700 ${className}`}
      title={`This was under the previous owner, ${name}`}
    >
      prev. owner · {name}
    </span>
  );
}
